import {
  ContextualSaveBar,
  Layout,
  Page,
  Card,
  BlockStack,
  Loading,
  SkeletonBodyText,
} from "@medentic/polaris-react";
import OverviewCard from "./components/OverviewCard";
import { AppPageWrapper } from "@shared/ui";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useState, useCallback, useMemo } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useCountries } from "@shared/hooks/common/useCountries";
import { 
  AttendeeDetail, 
  AttendeeEvent,
  Language,
  EventCategory
} from "@features/events/types";
import { PlusIcon, DeleteIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import CompanyInfoCard from "./components/CompanyInfoCard";
import EventParticipationCard from "./components/EventParticipationCard";
import HistoryCard from "./components/HistoryCard";
import SummaryCard from "./components/SummaryCard";
import TopicsOfInterestCard from "./components/TopicsOfInterestCard";
import { NoteTone } from "@shared/ui";
import AttendeeNotes from "./components/AttendeeNotes";
import { TagsCard } from "@shared/ui";
import AssignToEventModal from "./components/AssignToEventModal";
import useEventMetadata from "@features/events/hooks/useEventMetadata";
import { handleInlineErrors, clearFieldErrors } from "@shared/lib";
import { showToast } from "@shared/lib";
import { useDispatch } from "react-redux";
import usePageMeta from "@shared/hooks/common/usePageMeta";

const NEW_ATTENDEE_INITIAL_DATA: Partial<AttendeeDetail> = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  phoneCountryCode: "+359",
  country: "BGR",
  city: "",
  dateOfBirth: "",
  languageCode: "bg",
  agreedMarketingEmail: false,
  agreedMarketingSMS: false,
  lastVisitAt: "",
  paymentsTotalSpent: 0,
  totalVisitedEvents: 0,
  isCompany: false,
  companyName: "",
  companyTaxId: "",
  companyAddress: "",
  companyCountry: "BGR",
  privateNote: "",
  privateNoteTone: "default",
  topicsOfInterestIds: [],
  tagIds: [],
};

function EventAttendeeDetail() {
  const { t } = useTranslation();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [attendeeDataDraft, setAttendeeDataDraft] = useState<
    Partial<AttendeeDetail> | null
  >(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { eventOrganizerId: hcc, attendeeId } = useParams<{
    attendeeId: string;
    eventOrganizerId: string;
  }>();

  const isNewAttendee = attendeeId === "new";
  const navigate = useNavigate();
  const location = useLocation();
  const { GET, PUT, POST, DELETE } = useApiRequest();
  const queryClient = useQueryClient();
  const { toneOptions } = useEventMetadata();
  const dispatch = useDispatch();
  
  // Fetch attendee details
  const {
    data: attendee,
    isLoading: isLoadingAttendee,
    isFetching: isFetchingAttendee,
  } = useQuery<AttendeeDetail>({
    queryKey: ["attendeeDetails", attendeeId],
    queryFn: () =>
      GET(`/api/healthcare-companies/${hcc}/e/attendees/${attendeeId}`),
    enabled: Boolean(hcc && attendeeId && !isNewAttendee),
    placeholderData: keepPreviousData,
  });

  // Fetch attendee events
  const { data: attendeeEvents = [] } = useQuery<AttendeeEvent[]>({
    queryKey: ["attendeeEvents", attendeeId],
    queryFn: () =>
      GET(`/api/healthcare-companies/${hcc}/e/attendees/${attendeeId}/events`),
    enabled: Boolean(hcc && attendeeId && !isNewAttendee),
  });

  // Fetch metadata
  const { countries } = useCountries();

  const { data: languages = [] } = useQuery<Language[]>({
    queryKey: ["languages"],
    queryFn: () => GET("/api/metadata/languages"),
  });

  const { metadata, isLoading: isLoadingMetadata } = useEventMetadata();
  const categories: EventCategory[] = metadata?.categories || [];

  // Prepare page meta data
  const attendeeName = attendee ? `${attendee.firstName || ''} ${attendee.lastName || ''}`.trim() : undefined;
  
  // Update page meta when attendee data is available
  usePageMeta(attendeeName);

  const initialAttendeeData = useMemo<Partial<AttendeeDetail>>(() => {
    if (isNewAttendee) {
      return NEW_ATTENDEE_INITIAL_DATA;
    }
    if (attendee && attendee.id) {
      return { ...attendee, events: attendeeEvents };
    }
    return {};
  }, [isNewAttendee, attendee, attendeeEvents]);

  const attendeeData = attendeeDataDraft ?? initialAttendeeData;

  // Update attendee data
  const handleDataChange = useCallback((updates: Partial<AttendeeDetail>) => {
    let finalUpdates = { ...updates };

    // If isCompany is being set to true, provide default values for required company fields
    if (updates.isCompany === true) {
      finalUpdates = {
        ...finalUpdates,
        companyName: finalUpdates.companyName || "",
        companyTaxId: finalUpdates.companyTaxId || "",
        companyAddress: finalUpdates.companyAddress || "",
        companyCountry: finalUpdates.companyCountry || "",
      };
    }

    setAttendeeDataDraft((prev) => ({
      ...(prev ?? initialAttendeeData),
      ...finalUpdates,
    }));
    setHasUnsavedChanges(true);

    // Clear field errors for updated fields
    const updatedFields = Object.keys(finalUpdates);
    clearFieldErrors(updatedFields, setFieldErrors);
  }, [initialAttendeeData]);

  // Save attendee mutation
  const { mutate: saveAttendeeMutation, isPending: isSaving } = useMutation({
    mutationFn: (data: Partial<AttendeeDetail>) => {
      if (isNewAttendee) {
        return POST(`/api/healthcare-companies/${hcc}/e/attendees`, data);
      } else {
        return PUT(
          `/api/healthcare-companies/${hcc}/e/attendees/${attendeeId}`,
          data
        );
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: (savedAttendee: any) => {
      queryClient.invalidateQueries({
        queryKey: ["attendeeDetails", attendeeId],
      });
      queryClient.invalidateQueries({ queryKey: ["attendees"] });
      queryClient.invalidateQueries({ queryKey: ["attendeeEvents", attendeeId] });
      setAttendeeDataDraft(null);
      setHasUnsavedChanges(false);

      dispatch(
        showToast({
          message: t("attendeeDetails.saveSuccess"),
          type: "success",
        })
      );
      if (isNewAttendee && savedAttendee?.id) {
        navigate(`/e/${hcc}/attendees/${savedAttendee.id}`);
      }
    },
    onError: (err) => {
      handleInlineErrors(err, setFieldErrors);
    },
  });

  const handleSave = useCallback(() => {
    let dataToSave = { ...attendeeData };

    // Ensure company fields are included when isCompany is true
    if (dataToSave.isCompany) {
      dataToSave = {
        ...dataToSave,
        companyName: dataToSave.companyName || "",
        companyTaxId: dataToSave.companyTaxId || "",
        companyAddress: dataToSave.companyAddress || "",
        companyCountry: dataToSave.companyCountry || "BGR",
      };
    }

    saveAttendeeMutation(dataToSave);
  }, [saveAttendeeMutation, attendeeData]);

  const handleDiscard = useCallback(() => {
    if (isNewAttendee) {
      navigate(`/e/${hcc}/attendees${location.search}`);
    } else {
      setAttendeeDataDraft(null);
      setHasUnsavedChanges(false);
    }
  }, [isNewAttendee, navigate, hcc, location.search]);

  // Handler for assign to event
  const handleAssignToEvent = useCallback(() => {
    setAssignModalOpen(true);
  }, []);

  const handleAssignModalClose = useCallback(() => {
    setAssignModalOpen(false);
  }, []);

  const handleAssignSuccess = useCallback(() => {
    // Refetch attendee events to show the new assignment
    queryClient.invalidateQueries({
      queryKey: ["attendeeEvents", attendeeId],
    });
    queryClient.invalidateQueries({
      queryKey: ["attendeeDetails", attendeeId],
    });
    queryClient.invalidateQueries({
      queryKey: ["attendees"],
    });
    setAssignModalOpen(false);
  }, [queryClient, attendeeId]);

  // Delete attendee mutation
  const { mutate: deleteAttendeeMutation, isPending: isDeleting } = useMutation(
    {
      mutationFn: () => {
        return DELETE(
          `/api/healthcare-companies/${hcc}/e/attendees/${attendeeId}`
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["attendees"] });

        navigate(`/e/${hcc}/attendees${location.search}`);
      },
    }
  );

  const handleDeleteAttendee = useCallback(() => {
    deleteAttendeeMutation();
  }, [deleteAttendeeMutation]);

  return (
    <AppPageWrapper>
      {hasUnsavedChanges && (
        <ContextualSaveBar
          backgroundColor="#2B0069"
          saveAction={{
            onAction: handleSave,
            loading: isSaving,
            disabled: false,
          }}
          discardAction={{
            onAction: handleDiscard,
          }}
        />
      )}
      <Page
        title={
          isNewAttendee
            ? t("attendeeDetails.newAttendee")
            : !isLoadingAttendee
            ? `${attendeeData?.firstName ?? ""} ${attendeeData?.lastName ?? ""}`
            : undefined
        }
        primaryAction={
          !isNewAttendee && {
            content: t("attendeeDetails.actions.assignToEvent"),
            onAction: handleAssignToEvent,
            icon: PlusIcon,
          }
        }
        actionGroups={
          !isNewAttendee
            ? [
                {
                  title: t("common.more"),
                  actions: [
                    {
                      content: t("eventDetails.more.delete"),
                      icon: DeleteIcon,
                      onAction: handleDeleteAttendee,
                      destructive: true,
                    },
                  ],
                },
              ]
            : []
        }
        backAction={{
          content: t("attendees.title"),
          url: `/e/${hcc}/attendees${location.search}`,
        }}
      >
        {(isLoadingAttendee ||
          isFetchingAttendee ||
          isSaving ||
          isDeleting) && <Loading />}
        <Layout>
          <Layout.Section>
            <BlockStack gap="400">
              <Card>
                {isLoadingAttendee ? (
                  <SkeletonBodyText lines={3} />
                ) : (
                  <OverviewCard
                    attendee={attendeeData}
                    countries={countries}
                    languages={languages}
                    onDataChange={handleDataChange}
                    loading={isLoadingAttendee}
                    fieldErrors={fieldErrors}
                  />
                )}
              </Card>

              <Card>
                <CompanyInfoCard
                  attendee={attendeeData}
                  countries={countries}
                  onDataChange={handleDataChange}
                  loading={isLoadingAttendee}
                  fieldErrors={fieldErrors}
                />
              </Card>

              {!isNewAttendee && (
                <>
                  <Card>
                    <EventParticipationCard
                      events={attendeeData?.events || []}
                      loading={isLoadingAttendee}
                      onAssignToEvent={handleAssignToEvent}
                    />
                  </Card>

                  <Card>
                    <HistoryCard
                      history={attendeeData?.history || []}
                      loading={isLoadingAttendee}
                    />
                  </Card>
                </>
              )}
            </BlockStack>
          </Layout.Section>

          <Layout.Section variant="oneThird">
            <BlockStack gap="400">
              <Card>
                <SummaryCard
                  attendee={attendeeData}
                  loading={isLoadingAttendee}
                />
              </Card>

              <Card>
                <TopicsOfInterestCard
                  selectedTopics={attendeeData?.topicsOfInterestIds || []}
                  categories={categories}
                  onDataChange={handleDataChange}
                  loading={isLoadingAttendee || isLoadingMetadata}
                />
              </Card>

              <Card>
                <AttendeeNotes
                  notes={attendeeData?.privateNote || ""}
                  tone={
                    (attendeeData?.privateNoteTone as NoteTone) || "default"
                  }
                  loading={isLoadingAttendee}
                  onDataChange={handleDataChange}
                  toneOptions={toneOptions}
                />
              </Card>

              <Card>
                <TagsCard
                  selectedTags={attendeeData?.tagIds || []}
                  onTagsUpdate={(tagIds) => handleDataChange({ tagIds })}
                  loading={isLoadingAttendee}
                  hccId={hcc}
                  context="attendees"
                />
              </Card>
            </BlockStack>
          </Layout.Section>
        </Layout>

        <AssignToEventModal
          open={assignModalOpen}
          onClose={handleAssignModalClose}
          attendeeId={attendeeId}
          attendeeName={`${attendeeData?.firstName || ""} ${
            attendeeData?.lastName || ""
          }`.trim()}
          onSuccess={handleAssignSuccess}
        />
      </Page>
    </AppPageWrapper>
  );
}

export default EventAttendeeDetail;
